import React, { useState } from 'react';
import './styles.scss';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { scheme } from './validation';
import { Flex, LoadingOverlay, Text, TextInput, Textarea } from '@mantine/core';
import Button from '../../components/elements/Button';
import { DatePickerInput } from '@mantine/dates';
import * as Unicons from '@iconscout/react-unicons';
import dayjs from 'dayjs';
import DropzoneInput from '../../components/elements/DropzoneInput';
import { convertToUTC } from '../../utilities/convertToUTC';

interface FormBadgeProps {
  type?: string;
  data?: any;
  loading?: boolean;
  onClose: () => void;
  handleSubmitData: (data: any) => void;
}

interface MasterBadgeData {
  badgeName?: string;
  badgeDescription?: string;
  startDate?: string;
  endDate?: string;
  badgeImage?: string;
}

const FormBadge: React.FC<FormBadgeProps> = ({
  onClose,
  loading,
  type,
  data,
  handleSubmitData
}) => {
  const [valueStartDate, setValueStartDate] = useState<Date | null>(
    data?.startDate ? new Date(data?.startDate) : null
  );

  const [valueEndDate, setValueEndDate] = useState<Date | null>(
    data?.endDate ? new Date(data?.endDate) : null
  );

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<MasterBadgeData>({
    defaultValues: {
      badgeName: data?.badgeName || '',
      badgeImage: data?.badgeImage || null,
      badgeDescription: data?.badgeDescription || '',
      startDate: data?.startDate || '',
      endDate: data?.endDate || ''
    },
    resolver: yupResolver(scheme)
  });

  const handleChangeDate = (date: any, type: string, field: any) => {
    field.onChange(date);
    if (type === 'startDate') setValueStartDate(date);
    if (type === 'endDate') setValueEndDate(date);
    return;
  };

  const handleSubmitForm: SubmitHandler<MasterBadgeData> = (values) => {
    values.endDate = convertToUTC(values.endDate);
    values.startDate = convertToUTC(values.startDate);
    handleSubmitData(values);
  };

  const handleDrop = (files: File[], base64: string, field: any) => {
    const badgeImage = { fileName: files[0].name, base64: base64 };
    field.onChange(JSON.stringify(badgeImage));
  };

  return (
    <>
      <section className="master-category-content__form">
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          {/* Text Input */}
          <TextInput
            label="Badge Name"
            radius={10}
            withAsterisk
            placeholder="Input badge name"
            error={errors.badgeName?.message}
            {...register('badgeName')}
          />

          <Textarea
            label="Badge Description"
            radius={10}
            withAsterisk
            placeholder="Input badge description"
            error={errors.badgeDescription?.message}
            {...register('badgeDescription')}
          />

          <Controller
            control={control}
            rules={{
              required: true
            }}
            render={({ field }) => (
              <Flex flex={1} direction="column">
                <DatePickerInput
                  className={
                    errors.startDate?.message &&
                    'schedule-rater__form__selectinput--error'
                  }
                  style={{
                    width: '100%'
                  }}
                  label="Start Date"
                  withAsterisk
                  placeholder="Select Date"
                  onChange={(date) =>
                    handleChangeDate(date, 'startDate', field)
                  }
                  minDate={dayjs().toDate()}
                  value={valueStartDate}
                  rightSection={<Unicons.UilCalendarAlt size={20} />}
                />
                {errors?.startDate?.message && (
                  <Text size="xs" c="red" fw={400}>
                    {errors.startDate.message}
                  </Text>
                )}
              </Flex>
            )}
            name="startDate"
          />
          <Controller
            control={control}
            rules={{
              required: true
            }}
            render={({ field }) => (
              <Flex flex={1} direction="column">
                <DatePickerInput
                  className={
                    errors.endDate?.message &&
                    'schedule-rater__form__selectinput--error'
                  }
                  style={{
                    width: '100%'
                  }}
                  label="End Date"
                  withAsterisk
                  placeholder="Select Date"
                  onChange={(date) => handleChangeDate(date, 'endDate', field)}
                  minDate={dayjs().toDate()}
                  value={valueEndDate}
                  rightSection={<Unicons.UilCalendarAlt size={20} />}
                />
                {errors?.endDate?.message && (
                  <Text size="xs" c="red" fw={400}>
                    {errors.endDate.message}
                  </Text>
                )}
              </Flex>
            )}
            name="endDate"
          />
          <Controller
            control={control}
            rules={{
              required: true
            }}
            render={({ field }) => (
              <DropzoneInput
                label="Badge Image"
                withAsterik
                onHandleDrop={(value, base64) =>
                  handleDrop(value, base64, field)
                }
                error={errors?.badgeImage?.message || ''}
                defaultValue={data?.badgeImage || null}
              />
            )}
            name="badgeImage"
          />

          <Flex justify="flex-end" align="center" gap={16} mt={32}>
            <Button variant="error-beelajar" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={loading} type="submit">
              {type === 'add' ? 'Submit' : 'Update'}
            </Button>
          </Flex>
        </form>
      </section>
      <LoadingOverlay visible={loading} />
    </>
  );
};

function convertToLocalTime(dateString) {
  const date = new Date(dateString);
  const localDate = date.toLocaleString();
  return localDate;
}

export default FormBadge;
